import { useEffect, useState } from 'react';
import { TRACKS, CAREER_PATHS } from '../../constants/appConstants.js';
import {
  getLearnerState,
  getLesson,
  getFirstLesson,
  getLastMoodCheckin,
  logMoodCheckin,
  hasEngagedLesson1,
} from '../../services/supabaseClient.js';

// ─────────────────────────────────────────────────────────────────
// MemoryHero — the dashboard home. M[Emory] leads with a conversational,
// adaptive greeting and ONE primary action; a slim certification-path strip
// gives orientation. Replaces the old busy card grid (mastery / daily-goal /
// salary). M[Emory]'s copy never says "Start a Lesson" — the action is its own
// button. Mood is folded in as an optional one-tap row (feeds pacing).
// ─────────────────────────────────────────────────────────────────

const TWENTY_FOUR_H = 24 * 60 * 60 * 1000;
const MOODS = [
  { mood: 'stressed',  score: 1, emoji: '😣', label: 'Stressed'  },
  { mood: 'tired',     score: 2, emoji: '😕', label: 'Tired'     },
  { mood: 'okay',      score: 3, emoji: '😐', label: 'Okay'      },
  { mood: 'good',      score: 4, emoji: '🙂', label: 'Good'      },
  { mood: 'motivated', score: 5, emoji: '🔥', label: 'Motivated' },
];
const shortLabel = (l) => (l || '').replace(/^(CompTIA|Microsoft|Google|AWS|Cisco|Red Hat)\s+/i, '');
const pathFor = (id) => CAREER_PATHS[id] || (id ? [id] : []);

function Orb() {
  return (
    <span style={{
      width: 26, height: 26, borderRadius: '50%', flexShrink: 0, display: 'inline-block',
      background: 'radial-gradient(circle at 35% 30%, #34d399, var(--brand-emerald))',
      boxShadow: '0 0 0 5px rgba(5,150,105,0.12)',
    }} />
  );
}

// Slim cert‑path strip: foundation → target, current track highlighted. Reads
// the onboarding cert_path, falling back to the current track's ladder.
function PathStrip({ learner, trackId }) {
  const ladder = (Array.isArray(learner?.cert_path) && learner.cert_path.length)
    ? learner.cert_path
    : pathFor(trackId);
  const chain = [...new Set([trackId, ...ladder].filter(Boolean))];
  if (chain.length === 0) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 24, paddingTop: 18, borderTop: '1px solid #eef0f4' }}>
      <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', color: 'var(--brand-slate)' }}>Your path</span>
      {chain.map((id, i) => {
        const current = id === trackId;
        return (
          <span key={id} style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            {i > 0 && <span aria-hidden style={{ color: '#cbd2dc', fontWeight: 700 }}>──</span>}
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 700, color: current ? 'var(--brand-amber)' : 'var(--brand-slate)' }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: current ? 'var(--brand-amber)' : '#cbd2dc' }} />
              {shortLabel(TRACKS[id]?.label || id)}
            </span>
          </span>
        );
      })}
    </div>
  );
}

export default function MemoryHero({ learner, onSelectTrack, onNavigate }) {
  const trackId = learner?.current_track;
  const track = trackId ? TRACKS[trackId] : null;
  const [state, setState]     = useState(null);
  const [lesson, setLesson]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [moodNeeded, setMoodNeeded] = useState(false);
  const [picked, setPicked]   = useState(null);
  // null = not resolved yet. true once the learner has TRULY engaged with
  // Lesson 1 (durable stamp, or a completed lesson / checkpoint attempt). Drives
  // the first-run card — see firstRun below. Re-read on every mount, so engaging
  // in the study console and returning Home hides the card without a reload.
  const [engaged, setEngaged] = useState(null);

  useEffect(() => {
    if (!learner?.id || !trackId) { setLoading(false); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const [st, lastMood, eng] = await Promise.all([
          getLearnerState(learner.id, trackId),
          getLastMoodCheckin(learner.id),
          hasEngagedLesson1(learner.id),
        ]);
        if (cancelled) return;
        setState(st);
        setEngaged(eng);
        const fresh = lastMood?.created_at && (Date.now() - new Date(lastMood.created_at).getTime()) < TWENTY_FOUR_H;
        setMoodNeeded(!fresh);

        // Bleed-guarded lesson resolution (mirrors NextBestActionCard).
        let l = null;
        if (st?.current_lesson_id) {
          const cand = await getLesson(st.current_lesson_id);
          if (cancelled) return;
          if (cand?.modules?.track_id === trackId) l = cand;
        }
        if (!l) { try { l = await getFirstLesson(trackId); } catch { /* leave null */ } }
        if (!cancelled) setLesson(l);
      } catch { /* fail silent */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [learner?.id, trackId]);

  const firstName   = (learner?.first_name || learner?.name || '').split(' ')[0];
  const started     = !!state?.current_lesson_id;
  const lessonTitle = lesson?.title;
  const namePart    = firstName ? `, ${firstName}` : '';
  // First run = the learner has a personalized track but has NOT yet truly
  // engaged with Lesson 1. Critically this keys off `engaged`, NOT "lesson
  // opened" (started): Clarity showed users opening Lesson 1 for 3–4s and
  // bouncing — a false activation. The card now persists through an open-and-
  // leave and only clears on real engagement (30s dwell, Continue, practice,
  // checkpoint, completion). While `engaged` is unresolved (null) we don't claim
  // first-run, so the card never flashes before the durable check returns.
  const firstRun    = !!trackId && !loading && engaged === false;

  let greeting;
  if (!trackId)            greeting = "Let's set up your path so I can tailor everything to your goal.";
  else if (loading)        greeting = `Good to see you${namePart}.`;
  else if (firstRun && lessonTitle) greeting = `Start your first lesson${namePart} — ${lessonTitle}.`;
  else if (firstRun)       greeting = `Start your first lesson${namePart}.`;
  else if (started && lessonTitle) greeting = `You're in ${lessonTitle}${namePart}. Let's keep the momentum going.`;
  else if (lessonTitle)    greeting = `Ready when you are${namePart} — we'll start with ${lessonTitle}.`;
  else                     greeting = `Ready when you are${namePart}. Let's get into it.`;

  // The value promise that earns the first click — only on the first run.
  // Spells out the loop so the 2–3 minutes feel concrete: teach → check → repair.
  const subline = firstRun
    ? "Give us 2–3 minutes to show you how AdaptOS helps you get certified faster. It will teach one concept, check what you understood, and remember what to repair next."
    : null;

  const ctaLabel = firstRun ? 'Start Lesson 1 →' : started ? 'Continue lesson →' : 'Start your first lesson →';
  const handleStart = () => { if (track) onSelectTrack?.({ id: trackId, ...track }); };

  const pickMood = async (m) => {
    if (picked) return;
    setPicked(m);
    try { await logMoodCheckin(learner.id, { mood: m.mood, score: m.score, trackId }); } catch { /* fire-and-forget */ }
  };

  return (
    <div style={{ maxWidth: 760, margin: '0 auto' }}>
      <div style={{
        background: 'linear-gradient(180deg, #ffffff, #fbfdfc)',
        border: '1px solid #eef0f4', borderRadius: 20,
        padding: 'clamp(24px, 4vw, 40px)', boxShadow: '0 8px 30px rgba(5,150,105,0.06)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Orb />
          <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', color: 'var(--brand-emerald)' }}>M[Emory]</span>
        </div>

        <p style={{ fontSize: 'clamp(19px, 2.6vw, 25px)', fontWeight: 600, color: '#1e1e2e', margin: '18px 0 0', lineHeight: 1.4 }}>
          {greeting}
        </p>

        {subline && (
          <p style={{ fontSize: 'clamp(14px, 1.7vw, 16px)', fontWeight: 500, color: 'var(--brand-slate)', margin: '10px 0 0', lineHeight: 1.5 }}>
            {subline}
          </p>
        )}

        <button
          type="button"
          onClick={trackId ? handleStart : () => onNavigate?.('roadmap')}
          style={{
            marginTop: 22, display: 'inline-flex', alignItems: 'center', gap: 8,
            background: 'var(--brand-amber)', color: '#fff', border: 'none', borderRadius: 12,
            padding: '14px 28px', fontSize: 15, fontWeight: 700, cursor: 'pointer',
            fontFamily: 'inherit', boxShadow: '0 6px 20px rgba(217,119,6,0.26)',
          }}
        >
          {trackId ? ctaLabel : 'Set up my path →'}
        </button>

        {/* Optional one-tap mood — folded into M[Emory], feeds pacing. */}
        {trackId && moodNeeded && !picked && (
          <div style={{ marginTop: 22 }}>
            <p style={{ fontSize: 12.5, color: 'var(--brand-slate)', margin: '0 0 9px' }}>
              How are you feeling today? <span style={{ color: 'var(--text-muted)' }}>(optional — helps me pace you)</span>
            </p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {MOODS.map(m => (
                <button key={m.mood} type="button" onClick={() => pickMood(m)} title={m.label} aria-label={m.label}
                  style={{ width: 46, height: 46, borderRadius: 12, border: '1px solid #e5e7eb', background: '#fff', fontSize: 21, lineHeight: 1, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.12s' }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--brand-emerald)'; e.currentTarget.style.background = 'rgba(5,150,105,0.06)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.background = '#fff'; }}
                >{m.emoji}</button>
              ))}
            </div>
          </div>
        )}
        {picked && (
          <p style={{ fontSize: 12.5, color: 'var(--brand-emerald)', fontWeight: 600, margin: '16px 0 0' }}>
            Got it — I'll keep that in mind. {picked.emoji}
          </p>
        )}

        {trackId && <PathStrip learner={learner} trackId={trackId} />}
      </div>
    </div>
  );
}
